import React, { useState, useEffect, useMemo } from 'react';
import { ProjectService } from '../services/projectService';
import { Table, Button, SearchBar, NCBadge } from './ui';
import FormattedDateInput from './ui/FormattedDateInput';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

/**
 * InvoicingBoard
 *
 * Standardized invoicing interface using the UI component library.
 *
 * Features:
 * - Server-side pagination (20/50/All)
 * - Server-side search with debouncing
 * - Server-side sorting
 * - Inline editing for quick updates
 * - Financial summary cards
 * - Billing status filters
 */
const InvoicingBoard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('jobNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20); // Default to 20
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(true); // Auto-resize enabled by default
  const [billingStatusFilter, setBillingStatusFilter] = useState('all'); // all, ready, pending, invoiced, paid
  const [savingCells, setSavingCells] = useState(new Set());

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Initial load
  useEffect(() => {
    window.scrollTo(0, 0);
    loadProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load projects from server
  const loadProjects = async (page = currentPage, search = debouncedSearchQuery, size = pageSize, statusFilter = billingStatusFilter) => {
    try {
      setLoading(true);
      setError(null);
      const result = await ProjectService.getPaginatedInvoiceProjects(
        page,
        size,
        sortField,
        sortDirection,
        search,
        statusFilter
      );

      setProjects(result.data);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
      setCurrentPage(result.currentPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update when sort, search, or filter changes
  useEffect(() => {
    if (!loading) {
      setCurrentPage(1);
      loadProjects(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection, debouncedSearchQuery, billingStatusFilter]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      loadProjects(newPage);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    loadProjects(1, debouncedSearchQuery, newSize);
  };

  // Handle manual column resize - auto-disable auto-resize
  const handleManualResize = () => {
    if (autoResizeEnabled) {
      setAutoResizeEnabled(false);
    }
  };

  // Handle sort events from Table component
  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Handle field updates - inline editing
  const handleFieldUpdate = async (projectId, field, value) => {
    const cellKey = `${projectId}-${field}`;
    setSavingCells(prev => new Set(prev).add(cellKey));

    // Map frontend field names to database column names
    const fieldMapping = {
      'modelPrice': 'model_price',
      'billingStatus': 'billing_status',
      'invoiceDate': 'invoiced_date',
      'paidDate': 'paid_date',
      'poNumber': 'po_number'
    };

    const dbField = fieldMapping[field] || field;

    try {
      await ProjectService.updateProject(projectId, dbField, value);

      // Update local state with frontend field name
      setProjects(prevProjects =>
        prevProjects.map(p =>
          p.id === projectId ? { ...p, [field]: value } : p
        )
      );
    } catch (error) {
      console.error('Error updating project:', error);
      setError(`Failed to update ${field}: ${error.message}`);
      // Reload projects to ensure consistency
      loadProjects();
    } finally {
      setSavingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
    }
  };

  // Calculate totals (use tnRevenue for actual TN earnings)
  const totals = useMemo(() => {
    const quoted = projects
      .filter(p => p.billingStatus === 'Quoted')
      .reduce((sum, p) => sum + (parseFloat(p.tnRevenue ?? p.modelPrice) || 0), 0);

    const invoiced = projects
      .filter(p => p.billingStatus === 'Invoiced')
      .reduce((sum, p) => sum + (parseFloat(p.tnRevenue ?? p.modelPrice) || 0), 0);

    const paid = projects
      .filter(p => p.billingStatus === 'Paid')
      .reduce((sum, p) => sum + (parseFloat(p.tnRevenue ?? p.modelPrice) || 0), 0);

    return { quoted, invoiced, paid, total: quoted + invoiced + paid };
  }, [projects]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };


  // Column definitions
  const columns = [
    {
      id: 'jobNumber',
      label: 'Job #',
      field: 'jobNumber',
      width: 100,
      minWidth: 80,
      maxWidth: 150,
      sortable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => <strong>{value}</strong>,
    },
    {
      id: 'projectName',
      label: 'Project',
      field: 'projectName',
      width: 250,
      minWidth: 150,
      maxWidth: 400,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'clientName',
      label: 'Client',
      field: 'clientName',
      width: 200,
      minWidth: 120,
      maxWidth: 300,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'modelPrice',
      label: 'Price',
      field: 'modelPrice',
      width: 120,
      minWidth: 100,
      maxWidth: 150,
      sortable: true,
      resizable: true,
      align: 'right',
      contentType: 'custom',
      render: (value, row) => {
        const isSaving = savingCells.has(`${row.id}-modelPrice`);
        const hasDiscount = row.discountPercentage > 0;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            {hasDiscount && (
              <NCBadge
                modelPrice={row.modelPrice}
                tnRevenue={row.tnRevenue}
                discountPercentage={row.discountPercentage}
                size="sm"
              />
            )}
            <input
              type="number"
              value={value || ''}
              onChange={(e) => handleFieldUpdate(row.id, 'modelPrice', e.target.value)}
              disabled={isSaving}
              placeholder="$0"
              style={{
                width: '100px',
                padding: '4px 8px',
                border: '1px solid transparent',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: 'transparent',
                color: 'var(--gray-50)',
                fontSize: 'var(--text-sm)',
                textAlign: 'right',
                transition: 'all var(--transition-fast)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--gray-600)';
                e.currentTarget.style.backgroundColor = 'var(--dark-bg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--cta-blue)';
                e.currentTarget.style.backgroundColor = 'var(--dark-bg)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            />
            {isSaving && <span style={{ fontSize: 'var(--text-xs)' }}>💾</span>}
          </div>
        );
      },
    },
    {
      id: 'billingStatus',
      label: 'Status',
      field: 'billingStatus',
      width: 140,
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      resizable: true,
      contentType: 'custom',
      render: (value, row) => {
        const isSaving = savingCells.has(`${row.id}-billingStatus`);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <select
              value={value || ''}
              onChange={(e) => handleFieldUpdate(row.id, 'billingStatus', e.target.value)}
              disabled={isSaving}
              style={{
                padding: '4px 8px',
                border: '1px solid transparent',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: 'transparent',
                color: 'var(--gray-50)',
                fontSize: 'var(--text-sm)',
                cursor: 'pointer',
                minWidth: '110px',
                transition: 'all var(--transition-fast)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--gray-600)';
                e.currentTarget.style.backgroundColor = 'var(--dark-bg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--cta-blue)';
                e.currentTarget.style.backgroundColor = 'var(--dark-bg)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <option value="">Not Set</option>
              <option value="Quoted">Quoted</option>
              <option value="Invoiced">Invoiced</option>
              <option value="Partial">Partial</option>
              <option value="Paid">Paid</option>
              <option value="Not Paid">Not Paid</option>
            </select>
            {isSaving && <span style={{ fontSize: 'var(--text-xs)' }}>💾</span>}
          </div>
        );
      },
    },
    {
      id: 'invoiceDate',
      label: 'Invoice Date',
      field: 'invoiceDate',
      width: 140,
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      resizable: true,
      contentType: 'custom',
      render: (value, row) => {
        const isSaving = savingCells.has(`${row.id}-invoiceDate`);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <FormattedDateInput
              value={value || ''}
              onChange={(newValue) => handleFieldUpdate(row.id, 'invoiceDate', newValue)}
              disabled={isSaving}
              className="date-input"
            />
            {isSaving && <span style={{ fontSize: 'var(--text-xs)' }}>💾</span>}
          </div>
        );
      },
    },
    {
      id: 'poNumber',
      label: 'PO Number',
      field: 'poNumber',
      width: 140,
      minWidth: 100,
      maxWidth: 200,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'custom',
      render: (value, row) => {
        const isSaving = savingCells.has(`${row.id}-poNumber`);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleFieldUpdate(row.id, 'poNumber', e.target.value)}
              disabled={isSaving}
              placeholder="PO #"
              style={{
                width: '100%',
                maxWidth: '120px',
                padding: '4px 8px',
                border: '1px solid transparent',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: 'transparent',
                color: 'var(--gray-50)',
                fontSize: 'var(--text-sm)',
                transition: 'all var(--transition-fast)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--gray-600)';
                e.currentTarget.style.backgroundColor = 'var(--dark-bg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = 'var(--cta-blue)';
                e.currentTarget.style.backgroundColor = 'var(--dark-bg)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            />
            {isSaving && <span style={{ fontSize: 'var(--text-xs)' }}>💾</span>}
          </div>
        );
      },
    },
    {
      id: 'paidDate',
      label: 'Paid Date',
      field: 'paidDate',
      width: 140,
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      resizable: true,
      contentType: 'custom',
      render: (value, row) => {
        const isSaving = savingCells.has(`${row.id}-paidDate`);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <FormattedDateInput
              value={value || ''}
              onChange={(newValue) => handleFieldUpdate(row.id, 'paidDate', newValue)}
              disabled={isSaving}
              className="date-input"
            />
            {isSaving && <span style={{ fontSize: 'var(--text-xs)' }}>💾</span>}
          </div>
        );
      },
    },
  ];

  if (loading && projects.length === 0) {
    return <LoadingSpinner message="Loading invoice projects..." />;
  }

  return (
    <div style={{ padding: 'var(--space-6)', backgroundColor: 'var(--dark-bg)', minHeight: '100vh' }}>
      {/* Breadcrumb Navigation */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-6)',
          fontSize: 'var(--text-sm)',
          color: 'var(--gray-400)',
        }}
      >
        <span style={{ fontWeight: 'var(--font-medium)' }}>3D Models</span>
        <span>{'>'}</span>
        <span style={{ color: 'var(--gray-200)', fontWeight: 'var(--font-semibold)' }}>Invoicing</span>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <ErrorMessage
            message={error}
            onRetry={loadProjects}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-6)',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}
          >
            Invoicing
          </h1>
          <p
            style={{
              margin: 0,
              marginTop: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-400)',
            }}
          >
            {totalCount} invoice-related projects
          </p>
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'var(--space-4)',
          marginBottom: 'var(--space-6)',
        }}
      >
        <div
          style={{
            padding: 'var(--space-4)',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'var(--dark-elevated)',
            border: '1px solid var(--gray-600)',
            borderLeft: '4px solid var(--warning)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--gray-400)',
              marginBottom: 'var(--space-1)',
              textTransform: 'uppercase',
              fontWeight: 'var(--font-semibold)',
            }}
          >
            Quoted
          </div>
          <div
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}
          >
            {formatCurrency(totals.quoted)}
          </div>
        </div>

        <div
          style={{
            padding: 'var(--space-4)',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'var(--dark-elevated)',
            border: '1px solid var(--gray-600)',
            borderLeft: '4px solid var(--cta-blue)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--gray-400)',
              marginBottom: 'var(--space-1)',
              textTransform: 'uppercase',
              fontWeight: 'var(--font-semibold)',
            }}
          >
            Invoiced
          </div>
          <div
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}
          >
            {formatCurrency(totals.invoiced)}
          </div>
        </div>

        <div
          style={{
            padding: 'var(--space-4)',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'var(--dark-elevated)',
            border: '1px solid var(--gray-600)',
            borderLeft: '4px solid var(--success)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--gray-400)',
              marginBottom: 'var(--space-1)',
              textTransform: 'uppercase',
              fontWeight: 'var(--font-semibold)',
            }}
          >
            Paid
          </div>
          <div
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}
          >
            {formatCurrency(totals.paid)}
          </div>
        </div>

        <div
          style={{
            padding: 'var(--space-4)',
            borderRadius: 'var(--radius-lg)',
            backgroundColor: 'rgba(20, 184, 166, 0.1)',
            border: '1px solid var(--primary-cyan)',
            borderLeft: '4px solid var(--primary-cyan)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--gray-400)',
              marginBottom: 'var(--space-1)',
              textTransform: 'uppercase',
              fontWeight: 'var(--font-semibold)',
            }}
          >
            Total
          </div>
          <div
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}
          >
            {formatCurrency(totals.total)}
          </div>
        </div>
      </div>

      {/* Billing Status Filter Buttons */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant={billingStatusFilter === 'all' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setBillingStatusFilter('all')}
        >
          All
        </Button>
        <Button
          variant={billingStatusFilter === 'ready' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setBillingStatusFilter('ready')}
        >
          Ready to Invoice
        </Button>
        <Button
          variant={billingStatusFilter === 'pending' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setBillingStatusFilter('pending')}
        >
          Quoted
        </Button>
        <Button
          variant={billingStatusFilter === 'invoiced' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setBillingStatusFilter('invoiced')}
        >
          Invoiced
        </Button>
        <Button
          variant={billingStatusFilter === 'paid' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setBillingStatusFilter('paid')}
        >
          Paid
        </Button>
      </div>

      {/* Table with integrated toolbar */}
      <div
        style={{
          border: '1px solid var(--gray-600)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        {/* Search Bar and Auto-resize Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-bg)',
            borderBottom: '1px solid var(--gray-600)',
            gap: 'var(--space-4)',
          }}
        >
          <div style={{ width: '300px', flexShrink: 0 }}>
            <SearchBar
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search projects, PO numbers..."
              size="sm"
            />
          </div>
          <Button
            variant={autoResizeEnabled ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setAutoResizeEnabled(!autoResizeEnabled)}
          >
            {autoResizeEnabled ? '✓ ' : ''}Auto-resize
          </Button>
        </div>

        <Table
          data={projects}
          columns={columns}
          sortable={true}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          searchable={false} // Using external server-side search
          resizable={true}
          paginated={false} // Handling pagination manually with server-side
          autoResizeOnDataChange={autoResizeEnabled}
          showAutoSizeButton={false} // Hide auto-size button, we have toggle in header
          storageKey="invoicing-table"
          loading={loading}
          emptyMessage={
            searchQuery
              ? 'No projects found matching your search'
              : 'No projects found for invoicing'
          }
          onManualResize={handleManualResize}
          variant="dense"
          stickyHeader={true}
          style={{ border: 'none', borderRadius: '0', overflow: 'visible' }}
        />

        {/* Custom Server-side Pagination */}
        {totalCount > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-bg)',
              borderTop: '1px solid var(--gray-600)',
              gap: 'var(--space-4)',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                Page {currentPage} of {totalPages} ({totalCount} total projects)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--gray-400)' }}>
                  Show:
                </span>
                <Button
                  variant={pageSize === 20 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(20)}
                >
                  20
                </Button>
                <Button
                  variant={pageSize === 50 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(50)}
                >
                  50
                </Button>
                <Button
                  variant={pageSize === 999999 ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => handlePageSizeChange(999999)}
                >
                  All
                </Button>
              </div>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, currentPage + 2);

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </Button>
                    );
                  }

                  return pages;
                })()}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicingBoard;
